import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import * as authApi from '../../api/authApi';
import { UmbLogo } from '../../components/layout/UmbLogo';

const MIN_PASSWORD_LENGTH = 8;

/**
 * Landing page for the emailed link `/reset-password?token=<jwt>`. The token is
 * only validated server-side on submit; a bad or used link shows the backend's
 * message and a way back to request a fresh one.
 */
export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = (searchParams.get('token') || '').trim();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [doneMessage, setDoneMessage] = useState(null);

  async function handleSubmit(event) {
    event.preventDefault();
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await authApi.resetPassword({ token, password });
      setDoneMessage(result?.message || 'Your password has been updated.');
    } catch (err) {
      setError(err.message || 'Could not reset password');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-eorig-surface px-4 py-10 font-sans text-eorig-ink [word-spacing:0.06em]">
      <div className="w-full max-w-md">
        <div className="rounded-2xl border border-eorig-border bg-white p-6 shadow-eorig-card sm:p-8">
          <UmbLogo />
          <h1 className="mt-6 text-2xl font-semibold tracking-normal text-eorig-ink">
            Choose a new password
          </h1>

          {!token ? (
            <div className="mt-4 space-y-3">
              <p className="text-sm text-red-600" role="alert">
                This reset link is missing its token. Open the link from your email
                again, or request a new one.
              </p>
              <Link
                to="/forgot-password"
                className="inline-block text-sm font-medium text-eorig-cyan underline"
              >
                Request a new reset link
              </Link>
            </div>
          ) : doneMessage ? (
            <div className="mt-6 space-y-4">
              <p
                role="status"
                className="rounded-xl border border-eorig-border bg-eorig-surface px-4 py-3 text-sm text-eorig-ink"
              >
                {doneMessage}
              </p>
              <Link
                to="/signin"
                className="block w-full rounded-xl bg-umb-navy px-4 py-2.5 text-center text-sm font-semibold text-white transition hover:opacity-95"
              >
                Go to sign in
              </Link>
            </div>
          ) : (
            <>
              <p className="mt-2 text-sm leading-relaxed text-eorig-muted">
                Your new password must be at least {MIN_PASSWORD_LENGTH} characters.
                Any other signed-in sessions will be signed out.
              </p>
              <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
                <label className="block text-sm">
                  <span className="mb-1.5 block font-medium text-eorig-ink">New password</span>
                  <input
                    type="password"
                    autoComplete="new-password"
                    required
                    minLength={MIN_PASSWORD_LENGTH}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full rounded-xl border border-eorig-border bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-eorig-cyan/35"
                  />
                </label>
                <label className="block text-sm">
                  <span className="mb-1.5 block font-medium text-eorig-ink">
                    Confirm new password
                  </span>
                  <input
                    type="password"
                    autoComplete="new-password"
                    required
                    minLength={MIN_PASSWORD_LENGTH}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="w-full rounded-xl border border-eorig-border bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-eorig-cyan/35"
                  />
                </label>
                {error ? (
                  <div className="space-y-1">
                    <p className="text-sm text-red-600" role="alert">
                      {error}
                    </p>
                    <Link
                      to="/forgot-password"
                      className="inline-block text-xs font-medium text-eorig-cyan underline"
                    >
                      Request a new reset link
                    </Link>
                  </div>
                ) : null}
                <button
                  type="submit"
                  disabled={busy}
                  className="w-full rounded-xl bg-umb-navy px-4 py-2.5 text-sm font-semibold text-white transition hover:opacity-95 disabled:opacity-60"
                >
                  {busy ? 'Updating…' : 'Set new password'}
                </button>
              </form>
            </>
          )}

          <p className="mt-4 text-center text-xs text-eorig-muted">
            <Link to="/signin" className="font-medium text-eorig-cyan underline">
              Back to sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
